import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  CreditCard,
  CheckCircle,
  ExternalLink,
  AlertTriangle,
  Loader2,
  Wallet
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * StripeConnectBanner - Reusable component for Stripe Connect onboarding
 * 
 * Shows:
 * - Banner prompting to connect if not set up
 * - Success state if connected
 * - Dashboard link if connected
 * 
 * Props:
 * - partnerType: "investor" | "realtor" | "contractor" | "lender" | "title"
 * - apiPrefix: The API prefix for the partner type (e.g., "/api/auth", "/api/realtor")
 * - onStatusChange: Callback when status changes
 */
export default function StripeConnectBanner({ 
  partnerType = "investor", 
  apiPrefix = "/api/auth",
  onStatusChange,
  compact = false 
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}${apiPrefix}/stripe/connect/status`,
        { withCredentials: true }
      );
      setStatus(response.data);
      if (onStatusChange) {
        onStatusChange(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch Stripe status:", error);
      setStatus({ onboarding_required: true });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setOnboarding(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}${apiPrefix}/stripe/connect/onboard`,
        {
          return_url: `${window.location.origin}${window.location.pathname}?stripe=connected`,
          refresh_url: `${window.location.origin}${window.location.pathname}?stripe=refresh`
        },
        { withCredentials: true }
      );

      if (response.data.already_connected && response.data.onboarding_complete) {
        toast.success("Stripe account already connected!");
        fetchStatus();
      } else if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start Stripe setup");
    } finally {
      setOnboarding(false);
    }
  };

  const handleViewDashboard = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}${apiPrefix}/stripe/dashboard`,
        { withCredentials: true }
      );
      if (response.data.dashboard_url) {
        window.open(response.data.dashboard_url, "_blank");
      }
    } catch (error) {
      toast.error("Could not open Stripe dashboard");
    }
  };

  if (loading) {
    return (
      <div className={`${compact ? 'p-3' : 'p-4'} bg-white/5 border border-white/[0.08] rounded-lg`}>
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 text-white/50 animate-spin" />
          <span className="text-white/50 text-sm">Checking payment setup...</span>
        </div>
      </div>
    );
  }

  // Connected and ready
  if (status?.onboarding_complete) {
    return (
      <div className={`${compact ? 'p-3' : 'p-4'} bg-emerald-500/10 border border-emerald-500/30 rounded-lg`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <div>
              <p className="text-emerald-400 font-medium text-sm">Stripe Connected</p>
              {!compact && (
                <p className="text-emerald-400/70 text-xs">
                  Ready to receive payments through DealForge
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={handleViewDashboard}
            size="sm"
            className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30"
          >
            <ExternalLink className="w-3 h-3 mr-1.5" />
            Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Connected but onboarding incomplete
  if (status?.connected && !status?.onboarding_complete) {
    return (
      <div className={`${compact ? 'p-3' : 'p-4'} bg-amber-500/10 border border-amber-500/30 rounded-lg`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <div>
              <p className="text-amber-400 font-medium text-sm">Complete Stripe Setup</p>
              {!compact && (
                <p className="text-amber-400/70 text-xs">
                  Finish onboarding to start receiving payments
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={handleConnect}
            disabled={onboarding}
            size="sm"
            className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
          >
            {onboarding ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <ExternalLink className="w-3 h-3 mr-1.5" />
                Continue Setup
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Not connected - show prominent banner
  const partnerLabels = {
    investor: "receive distributions and returns",
    realtor: "receive commissions",
    contractor: "receive draw payments",
    lender: "receive origination fees",
    title: "receive deal wallet funds and closing fees"
  };

  return (
    <div className={`${compact ? 'p-3' : 'p-4'} bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg`}>
      <div className={compact ? "flex items-center justify-between gap-4" : ""}>
        <div className={`flex items-center gap-3 ${compact ? '' : 'mb-3'}`}>
          <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
            <Wallet className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div>
            <p className="text-[#D4AF37] font-bold text-sm">Enable Payment Routing</p>
            {!compact && (
              <p className="text-[#D4AF37]/70 text-xs mt-0.5">
                Securely route transaction funds and receive assigned fees — DealForge does not hold funds.
              </p>
            )}
          </div>
        </div>
        
        {!compact && (
          <div className="space-y-2 text-xs text-white/50 mb-4 ml-13 pl-13">
            <p className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-emerald-400" />
              Secure direct deposits to your bank account
            </p>
            <p className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-emerald-400" />
              Real-time payment tracking and history
            </p>
            <p className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-emerald-400" />
              DealForge handles all payment routing automatically
            </p>
          </div>
        )}
        
        <Button
          onClick={handleConnect}
          disabled={onboarding}
          className={`${compact ? '' : 'w-full'} bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-bold`}
        >
          {onboarding ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CreditCard className="w-4 h-4 mr-2" />
          )}
          Connect Stripe
        </Button>
      </div>
    </div>
  );
}

// Compact inline version
export function StripeConnectInline({ partnerType, apiPrefix, onStatusChange }) {
  return (
    <StripeConnectBanner
      partnerType={partnerType}
      apiPrefix={apiPrefix}
      onStatusChange={onStatusChange}
      compact={true}
    />
  );
}
